import { useEffect, useState } from 'react';
import { analyticsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, TrendingUp, TrendingDown, Heart, DollarSign } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';

const COLORS = ['#7C3AED', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#60A5FA'];
const TIP_COLORS = ['#F59E0B', '#FB923C', '#FBBF24', '#FCD34D', '#9CA3AF'];

export default function AdminAnalytics() {
  const [revenue, setRevenue] = useState(null);
  const [orders, setOrders] = useState(null);
  const [products, setProducts] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [tips, setTips] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journey, setJourney] = useState(null);
  useEffect(() => { analyticsAPI.getJourney().then(res => setJourney(res.data)).catch(() => {}); }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [revenueRes, ordersRes, productsRes, peakRes, tipsRes] = await Promise.all([
          analyticsAPI.getRevenue(),
          analyticsAPI.getOrders(),
          analyticsAPI.getProducts(),
          analyticsAPI.getPeakHours(),
          analyticsAPI.getTips()
        ]);
        setRevenue(revenueRes.data);
        setOrders(ordersRes.data);
        setProducts(productsRes.data);
        setPeakHours(peakRes.data);
        setTips(tipsRes.data);
      } catch (err) {
        console.error('Error loading analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const formatHourlyData = () => {
    if (!peakHours?.hourly_distribution) return [];
    return Object.entries(peakHours.hourly_distribution).map(([hour, count]) => ({
      hour,
      commandes: count
    }));
  };

  const formatTopProducts = () => {
    if (!products?.top_selling) return [];
    return products.top_selling.slice(0, 5).map((p, idx) => ({
      name: p.name,
      value: p.quantity,
      fill: COLORS[idx % COLORS.length]
    }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {journey && <Card><CardHeader><CardTitle>Parcours client · 30 derniers jours</CardTitle></CardHeader><CardContent>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{[
          ['Menus consultés', journey.visits.menu_viewed || 0], ['Visites du panier', journey.visits.cart_viewed || 0],
          ['Menus sans panier', journey.menu_without_cart || 0], ['Échecs de paiement Stripe', journey.events.payment_failed || 0],
          ['Paiements expirés', journey.events.payment_expired || 0],
          ['Passages au paiement', journey.visits.checkout_started || 0], ['Paniers sans validation', journey.cart_without_checkout],
          ['Commandes transmises', journey.events.order_created || 0], ['Erreurs de validation/paiement', journey.events.checkout_error || 0],
          ['Suggestions ajoutées', journey.events.suggestion_added || 0], ['Ouvertures avis Google', journey.events.google_review_opened || 0]
        ].map(([label, value]) => <div key={label}><p className="text-sm text-slate-600">{label}</p><p className="text-2xl font-bold">{value}</p></div>)}</div>
        <p className="mt-4 text-xs text-slate-500">Les visites sont dédupliquées par session de navigateur. Les paniers sans validation incluent les visites encore en cours. Une ouverture Google ne prouve pas la publication d’un avis.</p>
      </CardContent></Card>}
      <div>
        <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
          Analytics
        </h1>
        <p className="text-gray-500">Analysez les performances de votre établissement</p>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 mb-1">Revenus aujourd'hui</p>
            <p className="text-3xl font-bold text-[#7C3AED]" style={{ fontFamily: 'JetBrains Mono' }}>
              {revenue?.today_revenue?.toFixed(2)}€
            </p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 mb-1">Revenus semaine</p>
            <p className="text-3xl font-bold text-[#3B82F6]" style={{ fontFamily: 'JetBrains Mono' }}>
              {revenue?.weekly_revenue?.toFixed(2)}€
            </p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 mb-1">Revenus mois</p>
            <p className="text-3xl font-bold text-[#8B5CF6]" style={{ fontFamily: 'JetBrains Mono' }}>
              {revenue?.monthly_revenue?.toFixed(2)}€
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 mb-1">Commandes aujourd'hui</p>
            <p className="text-2xl font-bold">{orders?.orders_today || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 mb-1">Total commandes</p>
            <p className="text-2xl font-bold">{orders?.total_orders || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 mb-1">Panier moyen</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
              {orders?.average_order_value?.toFixed(2)}€
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tips Section */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Heart className="w-5 h-5 text-amber-500" />
            Pourboires
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Aujourd'hui</p>
              <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono' }}>
                {tips?.today_tips?.toFixed(2) || '0.00'}€
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Cette semaine</p>
              <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono' }}>
                {tips?.weekly_tips?.toFixed(2) || '0.00'}€
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Ce mois</p>
              <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono' }}>
                {tips?.monthly_tips?.toFixed(2) || '0.00'}€
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-sm text-gray-500 mb-1">Pourboire moyen</p>
              <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono' }}>
                {tips?.average_tip?.toFixed(2) || '0.00'}€
              </p>
            </div>
          </div>

          {/* Tip Distribution */}
          {tips?.tip_distribution && (
            <div>
              <p className="text-sm font-medium text-gray-600 mb-3">Répartition des pourboires</p>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(tips.tip_distribution).map(([label, count], idx) => (
                  <div 
                    key={label}
                    className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm"
                  >
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: TIP_COLORS[idx % TIP_COLORS.length] }}
                    />
                    <span className="text-sm font-medium">{label}</span>
                    <Badge variant="secondary" className="text-xs">{count}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total tips count */}
          <div className="mt-4 pt-4 border-t border-amber-200 flex items-center justify-between">
            <span className="text-sm text-gray-600">Total pourboires reçus</span>
            <span className="font-bold text-amber-600">{tips?.tips_count_total || 0}</span>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Distribution */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Commandes par heure</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formatHourlyData()}>
                  <defs>
                    <linearGradient id="colorCommandes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#7C3AED" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: 'none', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                    }} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commandes" 
                    stroke="#7C3AED" 
                    fillOpacity={1}
                    fill="url(#colorCommandes)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Products Pie Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Produits les plus vendus</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex items-center">
              {formatTopProducts().length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatTopProducts()}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name} (${value})`}
                      labelLine={{ stroke: '#9CA3AF' }}
                    >
                      {formatTopProducts().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full text-center text-gray-400">
                  Pas encore de données
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Products List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Classement des produits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top Selling */}
            <div>
              <h4 className="font-semibold text-violet-600 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Meilleurs ventes
              </h4>
              <div className="space-y-2">
                {products?.top_selling?.slice(0, 5).map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="font-medium">{p.name}</span>
                    <div className="text-right">
                      <span className="text-sm text-gray-500">{p.quantity} vendus</span>
                      <span className="ml-2 font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                        {p.revenue?.toFixed(2)}€
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Least Selling */}
            <div>
              <h4 className="font-semibold text-red-600 mb-3 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" />
                À améliorer
              </h4>
              <div className="space-y-2">
                {products?.least_selling?.slice(0, 5).map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <span className="font-medium">{p.name}</span>
                    <div className="text-right">
                      <span className="text-sm text-gray-500">{p.quantity} vendus</span>
                      <span className="ml-2 font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                        {p.revenue?.toFixed(2)}€
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
